import React, { useState, useEffect } from 'react';
import { X, UserPlus, Trash2, RotateCcw, Save, Loader2, Users, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { updatePayrollRun, getAllEmployeeSalaries } from '../../api/payroll';

const EditPayrollRunModal = ({ isOpen, onClose, run, onSuccess, module = 'retail' }) => {
  const [description, setDescription] = useState('');
  const [removedPayslipIds, setRemovedPayslipIds] = useState(new Set());
  const [selectedAddEmployeeId, setSelectedAddEmployeeId] = useState('');
  const [addedEmployees, setAddedEmployees] = useState([]); // array of employee_salary objects
  const [allAvailableEmployees, setAllAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [saving, setSaving] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  useEffect(() => {
    if (run) {
      setDescription(run.description || '');
      setRemovedPayslipIds(new Set());
      setAddedEmployees([]);
      setSelectedAddEmployeeId('');
      fetchAvailableEmployees();
    }
  }, [run]);

  const fetchAvailableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const data = await getAllEmployeeSalaries({
        module: run?.module || module,
        isActive: true
      });
      setAllAvailableEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employee list:', error);
      toast.error('Failed to load employee directory');
    } finally {
      setLoadingEmployees(false);
    }
  };

  if (!isOpen || !run) return null;

  // Filter current payslips vs removed
  const currentPayslips = run.payslips || [];
  const existingEmployeeSalaryIds = currentPayslips
    .filter((p) => !removedPayslipIds.has(p.id))
    .map((p) => p.employee_salary_id || p.user_id);

  // Available employees to add (not in run, or marked for removal, or not yet selected)
  const selectableEmployees = allAvailableEmployees.filter((emp) => {
    const isAlreadyInRun = currentPayslips.some(
      (p) =>
        !removedPayslipIds.has(p.id) &&
        (p.employee_salary_id === emp.id || (p.user_id && p.user_id === emp.user_id))
    );
    const isAlreadyStaged = addedEmployees.some((staged) => staged.id === emp.id);
    return !isAlreadyInRun && !isAlreadyStaged;
  });

  const handleToggleRemove = (payslipId) => {
    const nextSet = new Set(removedPayslipIds);
    if (nextSet.has(payslipId)) {
      nextSet.delete(payslipId);
    } else {
      nextSet.add(payslipId);
    }
    setRemovedPayslipIds(nextSet);
  };

  const handleAddEmployee = () => {
    if (!selectedAddEmployeeId) return;
    const empToAdd = allAvailableEmployees.find(
      (e) => String(e.id) === String(selectedAddEmployeeId)
    );
    if (empToAdd) {
      setAddedEmployees([...addedEmployees, empToAdd]);
      setSelectedAddEmployeeId('');
    }
  };

  const handleRemoveStagedEmployee = (empId) => {
    setAddedEmployees(addedEmployees.filter((e) => e.id !== empId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        description: description.trim(),
        removePayslipIds: Array.from(removedPayslipIds),
        addEmployeeSalaryIds: addedEmployees.map((e) => e.id)
      };

      await updatePayrollRun(run.id, payload);
      toast.success('Payroll run updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating payroll run:', error);
      toast.error(error.response?.data?.message || 'Failed to update payroll run');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-primary-black">
              Edit Payroll Run {run.run_number ? `#${run.run_number}` : ''}
            </h2>
            <p className="text-xs text-secondary-gray mt-0.5">
              Modify description, remove staff, or add new staff to this payroll run.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-primary-black mb-1.5">
              Payroll Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. July 2026 Monthly Staff Salaries"
              className="w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold outline-none text-sm"
            />
          </div>

          {/* Add Employee Section */}
          <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-emerald-900 mb-2 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-emerald-600" />
              Add Employee to Run
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={selectedAddEmployeeId}
                onChange={(e) => setSelectedAddEmployeeId(e.target.value)}
                disabled={loadingEmployees || selectableEmployees.length === 0}
                className="flex-1 px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white text-sm outline-none"
              >
                <option value="">
                  {loadingEmployees
                    ? 'Loading employee directory...'
                    : selectableEmployees.length === 0
                      ? 'All active employees are already in this run'
                      : '-- Select Employee to Add --'}
                </option>
                {selectableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name} ({emp.branch_name || 'Main Branch'}) —{' '}
                    {formatCurrency(emp.gross_salary || emp.basic_salary)} gross
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddEmployee}
                disabled={!selectedAddEmployeeId}
                className="px-4 py-2 bg-emerald-600 text-white font-medium text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1.5 justify-center"
              >
                <UserPlus className="w-4 h-4" />
                Add Employee
              </button>
            </div>

            {/* Staged added employees list */}
            {addedEmployees.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-emerald-800">
                  Newly Added Employees (Will be calculated and saved):
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {addedEmployees.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between bg-white border border-emerald-200 p-2.5 rounded-lg shadow-sm"
                    >
                      <div>
                        <p className="text-xs font-bold text-gray-900">{emp.full_name}</p>
                        <p className="text-[11px] text-gray-500">
                          Basic: {formatCurrency(emp.basic_salary)} | Gross:{' '}
                          {formatCurrency(emp.gross_salary)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveStagedEmployee(emp.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove from staging"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Current Employees in Run */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-primary-black flex items-center gap-2">
                <Users className="w-4 h-4 text-accent-gold" />
                Current Employees in Run ({currentPayslips.length})
              </h3>
              {removedPayslipIds.size > 0 && (
                <span className="text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                  {removedPayslipIds.size} Marked for Removal
                </span>
              )}
            </div>

            <div className="border border-secondary-lightGray rounded-lg overflow-hidden max-h-72 overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 text-gray-700 sticky top-0 font-semibold border-b">
                  <tr>
                    <th className="p-3">Employee</th>
                    <th className="p-3">Department / Branch</th>
                    <th className="p-3 text-right">Gross</th>
                    <th className="p-3 text-right">Deductions</th>
                    <th className="p-3 text-right">Net Pay</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPayslips.map((ps) => {
                    const isRemoved = removedPayslipIds.has(ps.id);
                    return (
                      <tr
                        key={ps.id}
                        className={`transition-colors ${
                          isRemoved ? 'bg-red-50/60 line-through text-gray-400' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="p-3 font-medium text-gray-900">
                          {ps.employee_name}
                          {isRemoved && (
                            <span className="ml-2 no-underline inline-block text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                              REMOVED
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-gray-500">
                          {ps.employee_department || ps.employee_designation} (
                          {ps.employee_branch_name || 'Main'})
                        </td>
                        <td className="p-3 text-right font-medium">
                          {formatCurrency(ps.gross_earnings)}
                        </td>
                        <td className="p-3 text-right text-red-600">
                          -{formatCurrency(ps.total_deductions)}
                        </td>
                        <td className="p-3 text-right font-bold text-emerald-700">
                          {formatCurrency(ps.net_pay)}
                        </td>
                        <td className="p-3 text-center no-underline">
                          <button
                            type="button"
                            onClick={() => handleToggleRemove(ps.id)}
                            className={`px-2.5 py-1 rounded text-xs font-semibold flex items-center gap-1 mx-auto transition-colors ${
                              isRemoved
                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                            }`}
                          >
                            {isRemoved ? (
                              <>
                                <RotateCcw className="w-3 h-3" /> Restore
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-3 h-3" /> Remove
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {currentPayslips.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-gray-400">
                        No employees currently in this payroll run.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Banner for Paid Runs */}
          {run.status === 'paid' && (
            <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-2.5 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Note for Paid Payroll Run:</p>
                <p className="mt-0.5 text-amber-800">
                  Adding new staff will automatically generate their paid payslip, process loan
                  repayments, and post their individual cash flow outflow entry (`"
                  {description || run.description || run.run_number} for [Employee]"`). Removing
                  staff will revert their loan deductions and remove their cash flow outflow entry.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
          <div className="text-xs text-secondary-gray">
            Net Employees after save:{' '}
            <span className="font-bold text-gray-900">
              {currentPayslips.length - removedPayslipIds.size + addedEmployees.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-secondary-lightGray rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-accent-gold text-white font-medium text-sm rounded-lg hover:bg-accent-gold/90 transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving Changes...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save & Recalculate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPayrollRunModal;
